import { useState, useEffect, useContext } from "react";
import type {
  DtoBorrowerProgress,
  DtoCoBorrowerProgress,
} from "@porchpass/api-client";
import { useWebClientApi } from "src/api";
import { UserContext } from "src/context";
import { useApiErrorHandler } from "./useApiErrorHandler";

type ReturnProgressShape = Record<keyof DtoBorrowerProgress, boolean | string>;
interface SectionProgressData {
  [borrower_id: string]: Partial<ReturnProgressShape>;
}

const markCompletedSections = (
  obj: Partial<DtoBorrowerProgress | DtoCoBorrowerProgress>,
) => {
  const mappedObj: Partial<ReturnProgressShape> = {};
  let key: keyof DtoBorrowerProgress;
  for (key in obj) {
    const typedKey = key;
    const value = obj[typedKey as keyof typeof obj];
    if (value === "COMPLETE" || value === "SKIPPED") {
      mappedObj[typedKey] = true;
    } else if (value === "INCOMPLETE") {
      mappedObj[typedKey] = false;
    } else if (typeof value === "string") {
      mappedObj[typedKey] = value;
    }
  }
  return mappedObj;
};

export const useSectionProgress = () => {
  const [sectionProgress, setSectionProgress] = useState<SectionProgressData>(
    {},
  );
  const { userData } = useContext(UserContext);
  const api = useWebClientApi();
  const { handleApiError } = useApiErrorHandler();

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const data = await api.calculateProgress();
        const sections: SectionProgressData = {};
        if (data.borrower?.borrower_id) {
          sections[data.borrower.borrower_id] = markCompletedSections(
            data.borrower,
          );
        }
        if (data.co_borrowers) {
          for (const borrower of data.co_borrowers) {
            if (borrower.borrower_id) {
              sections[borrower.borrower_id] = markCompletedSections(borrower);
            }
          }
        }
        setSectionProgress(sections);
      } catch (err) {
        handleApiError(
          err instanceof Error ? err : new Error("Failed to fetch progress"),
        );
        console.log("SectionProgress", err);
      }
    };

    void fetchProgress();
  }, [api]);

  const borrowerId = userData?.borrower_id;
  return (borrowerId ? sectionProgress[borrowerId] : undefined) ?? {};
};
